import { stripResearchLinks, type AdminFoodDetail } from '@steado/admin-contract/foods';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { SparklesIcon } from 'lucide-react';
import { useState } from 'react';

import { SteadoLoader } from '@/components/shell/SteadoLoader';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/lib/auth/auth-context';
import { useDebounced } from '@/lib/use-debounced';
import { createFood, foodsQueryOptions, researchFood } from '@/screens/foods/foods-query';

import { FoodFields } from './FoodFields';
import { EMPTY_FOOD_FORM, formIssues, fromResearchDraft, toCreateRequest } from './food-form';

export interface NewFoodScreenProps {
  /** Prefilled from a food request, so the user's own wording is not retyped. */
  readonly initialName?: string | undefined;
  readonly onCreated: (food: AdminFoodDetail) => void;
  /** Where Discard goes. Absent leaves the page with no way out but the crumb. */
  readonly onDiscard?: () => void;
}

const ADMIN_ONLY = 'Creating a food requires the admin role.';

/**
 * Creating a food, on a page rather than in a modal.
 *
 * The same reasoning as the exercise create page it copies: a dialog sized for
 * six fields quietly becomes the definition of what a food has, and a food has
 * twenty. Everything the food drawer can edit is here, plus the identity and
 * nutrition a drawer cannot reach at all.
 *
 * The model's part is bounded on purpose, and the card that offers it says so
 * in the one place an operator will read it. Research fills the form and
 * nothing else — there is no "save this draft" path, the allergen provenance is
 * never ticked by a draft, and every figure is re-checked server-side against
 * the bounds the catalog engine binds on, so accepting a draft without reading it
 * cannot publish a number arithmetic would refuse.
 */
export function NewFoodScreen({ initialName, onCreated, onDiscard }: NewFoodScreenProps) {
  const { api, can } = useAuth();
  const queryClient = useQueryClient();
  // Seeded once, at mount. A prefill and nothing more: re-applying it when the
  // prop changed would overwrite what the operator had already typed, and the
  // route only ever supplies it on the way in.
  const [values, setValues] = useState({ ...EMPTY_FOOD_FORM, name: initialName ?? '' });
  const [warnings, setWarnings] = useState<string[]>([]);
  const [draftNote, setDraftNote] = useState<string | null>(null);
  /**
   * Kept outside the form values on purpose: this steers one research call and
   * is never part of the row, so it has no business in the shape `toCreateRequest`
   * reads. Two foods share a name often enough that a nudge is the difference
   * between a useful draft and a plausible one about the wrong dish.
   */
  const [hint, setHint] = useState('');

  const research = useMutation({
    mutationFn: () =>
      researchFood(api, {
        name: values.name.trim(),
        kind: values.kind,
        ...(hint.trim() === '' ? {} : { hint: hint.trim() }),
      }),
    onSuccess: (draft) => {
      setValues((current) => fromResearchDraft(current, draft));
      setWarnings(draft.warnings.map(stripResearchLinks).filter(Boolean));
      setDraftNote(stripResearchLinks(draft.note ?? ''));
    },
    retry: false,
  });

  const create = useMutation({
    mutationFn: () => createFood(api, toCreateRequest(values)),
    onSuccess: (food) => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'foods'] });
      onCreated(food);
    },
    retry: false,
  });

  const issues = formIssues(values);
  const named = values.name.trim();
  /**
   * The whole form is held while the bounded research request runs:
   * `fromResearchDraft` overwrites most of it on return, so anything typed in
   * the meantime would be lost without a word, and a disabled field says so
   * before it happens.
   */
  const researching = research.isPending;
  const blocked = issues.length > 0 || !can.mutate || researching;

  // Catches the duplicate before twenty fields are filled in rather than after
  // the server's 409. Same endpoint the catalog list uses, so no new API.
  const debouncedName = useDebounced(named, 320);
  const similar = useQuery({
    ...foodsQueryOptions(api, { search: debouncedName, activity: 'active' }),
    enabled: debouncedName.length >= 3,
  });
  const existing = (similar.data?.items ?? []).slice(0, 4);

  return (
    <div className="flex max-w-[820px] flex-col gap-3" aria-busy={researching}>
      <div className="flex flex-wrap items-center gap-2.5 rounded-xl border border-border bg-card px-4 py-3.5">
        {researching ? (
          // The brand loader, with the state carried by the text: under
          // reduced motion the mark sits still, and the sentence is what says
          // something is happening.
          <span role="status" className="flex min-w-0 flex-1 items-center gap-3">
            <SteadoLoader className="size-7 text-primary" />
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="text-[13px] font-semibold text-foreground">Researching {named}…</span>
              <span className="text-[12.5px] text-pretty text-muted-foreground">
                Checking the food, nutrition and Indian serving sizes. This can take up to 90 seconds. The
                draft will appear when research finishes.
              </span>
            </span>
          </span>
        ) : (
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="text-[13px] font-semibold text-foreground">Research this food</span>
            <span className="text-[12.5px] text-pretty text-muted-foreground">
              It only fills the fields below. Nothing is saved until you press Create food.
            </span>
          </span>
        )}
        <span className="flex min-w-[240px] flex-1 items-center gap-2">
          <Input
            aria-label="Research hint"
            value={hint}
            disabled={!can.mutate || researching}
            placeholder="Optional hint: Kerala style, the packaged one…"
            maxLength={200}
            onChange={(event) => {
              setHint(event.target.value);
            }}
            className="min-w-0 flex-1"
          />
          <Button
            type="button"
            onClick={() => {
              research.mutate();
            }}
            disabled={named.length < 2 || researching || !can.mutate}
            title={
              can.mutate
                ? named.length < 2
                  ? 'Type the name first — that is what it researches.'
                  : undefined
                : ADMIN_ONLY
            }
          >
            {researching ? (
              <SteadoLoader className="size-3.5" />
            ) : (
              <SparklesIcon aria-hidden="true" className="size-3.5" />
            )}
            {researching ? 'Researching…' : 'Fill with AI'}
          </Button>
        </span>
      </div>

      {research.isError ? (
        <Alert role="alert" tone="err">
          <AlertDescription>
            The model could not draft this food: {stripResearchLinks(research.error.message)}. Try again with
            a clearer hint, or fill the form in by hand.
          </AlertDescription>
        </Alert>
      ) : null}

      {warnings.length === 0 ? null : (
        <Alert tone="warn">
          <AlertDescription>
            <ul className="flex list-disc flex-col gap-1 pl-4">
              {warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {values.research === null ? null : (
        <Alert role="note" aria-label="Research draft basis" tone="warn">
          <AlertTitle>
            {values.research.nutritionBasis === 'estimated' || values.research.portionBasis === 'estimated'
              ? 'Estimated research draft'
              : 'Research draft basis'}
          </AlertTitle>
          <AlertDescription>
            <p>
              Draft nutrition: <strong>{values.research.nutritionBasis}</strong>. Draft portions:{' '}
              <strong>{values.research.portionBasis}</strong>. Food state:{' '}
              <strong>{values.research.foodState.replaceAll('_', ' ')}</strong>.
            </p>
            {values.research.summary === '' ? null : <p>{values.research.summary}</p>}
            {values.research.assumptions.length === 0 ? null : (
              <ul aria-label="Research draft assumptions" className="list-disc pl-4">
                {values.research.assumptions.map((assumption, index) => (
                  <li key={index}>{assumption}</li>
                ))}
              </ul>
            )}
            <p>
              These describe the research draft. Check the figures and assumptions; later edits are yours.
            </p>
          </AlertDescription>
        </Alert>
      )}

      {existing.length === 0 ? null : (
        <div className="rounded-xl border border-border bg-muted px-4 py-3 text-[13px] leading-relaxed text-muted-foreground">
          Already in the catalog:{' '}
          {existing.map((food, index) => (
            <span key={food.id}>
              {index === 0 ? '' : ', '}
              <Link
                to="/foods/$foodId"
                params={{ foodId: food.id }}
                className="font-medium text-foreground hover:text-primary"
              >
                {food.name}
              </Link>
            </span>
          ))}
          . Editing one of those is better than adding a second row for the same food — and the server will
          refuse a name it already holds.
        </div>
      )}

      <FoodFields
        values={values}
        onChange={(next) => {
          if (next.name !== values.name || next.kind !== values.kind) {
            // A different identity cannot inherit the previous food's evidence.
            setValues({
              ...next,
              research: null,
              recipeNote: next.recipeNote === draftNote ? '' : next.recipeNote,
            });
            setDraftNote(null);
            setWarnings([]);
          } else {
            setValues(next);
          }
        }}
        disabled={!can.mutate || researching}
      />

      {/*
        The action bar closes the page rather than heading it. A form this long
        is read downwards, and a Create button at the top is a button pressed
        before the bottom third has been looked at.
      */}
      <div className="mt-2.5 flex flex-wrap items-center gap-2.5 rounded-xl border border-border bg-card px-4 py-3.5">
        <span className="min-w-[260px] flex-1 text-[12.5px] text-pretty text-muted-foreground">
          {/* Stated rather than left to a disabled button: an operator should
              not have to guess which of twenty fields is holding Create shut. */}
          {researching
            ? 'Held while the draft is being researched.'
            : issues.length > 0
              ? `Before this can be created: ${issues.join(' ')}`
              : create.isError
                ? create.error.message
                : 'The row lands active and verified — filling this in is the sign-off, so there is no separate verify step. Every later change to it is in its History and can be reverted.'}
        </span>

        <span className="ml-auto flex items-center gap-2">
          {onDiscard === undefined ? null : (
            <Button type="button" variant="outline" onClick={onDiscard} disabled={researching}>
              Discard
            </Button>
          )}
          <Button
            type="button"
            onClick={() => {
              if (!blocked) create.mutate();
            }}
            disabled={blocked || create.isPending}
            title={can.mutate ? (issues.length === 0 ? undefined : issues.join(' ')) : ADMIN_ONLY}
          >
            {create.isPending ? 'Creating…' : 'Create food'}
          </Button>
        </span>
      </div>
    </div>
  );
}
